import React, { useEffect, useState } from "react";
import { Box, Typography, Paper } from "@/ui";
import DashboardLayout from "@/components/DashboardLayout";
import { useNavigate } from "react-router-dom";
import FreepostForm from "@/components/FreepostForm";
import { Company } from "../dashboard/SharePage";
import { getCustomerId } from "@/shared/lib/utils/getCustomerId";
import { SharePageService } from "@/shared/api/services/sharePageService";

const FreePostPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    const fetchCompanies = async (): Promise<Company[]> => {
      const resolvedCustomerId: string | number | null = await getCustomerId();

      if (!resolvedCustomerId) {
        return [];
      }

      try {
        const coerceNumericId = (value: unknown): number | null => {
          if (typeof value === "number" && Number.isFinite(value)) return value;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return null;
            const parsed = Number(trimmed);
            return Number.isFinite(parsed) ? parsed : null;
          }
          return null;
        };

        const isSharedRow = (row: any): boolean => {
          const submit =
            typeof row?.submitDate === "string" ? row.submitDate.trim() : "";
          const lastShared =
            typeof row?.lastSharedDate === "string"
              ? row.lastSharedDate.trim()
              : "";
          return Boolean(submit || lastShared);
        };

        /**
         * IMPORTANT (legacy parity):
         * - Keep ALL rows that were already shared (submitDate/lastSharedDate present),
         *   because history must remain visible (and reference edits after sharing create a new row).
         * - Only dedupe *pending/unshared* duplicates (no submitDate/lastSharedDate), since these
         *   are the "company shows twice" bug scenario.
         */
        const dedupeSharingRows = (rows: any[]): any[] => {
          const bestPendingByKey = new Map<string, any>();

          const keyForRow = (row: any): string => {
            const debtId = coerceNumericId(row?.debtId);
            const id = coerceNumericId(row?.id);
            if (typeof debtId === "number") return `debt:${debtId}`;
            if (typeof id === "number") return `id:${id}`;
            return `fallback:${String(row?.lenderName ?? row?.companyName ?? "")}|${String(
              row?.accountNumber ?? row?.referenceNumber ?? ""
            )}`;
          };

          // Pass 1: choose the "best" pending row per key (latest lastUpdateDate)
          for (const row of rows) {
            if (isSharedRow(row)) continue;
            const key = keyForRow(row);
            const prev = bestPendingByKey.get(key);
            const prevTs = prev?.lastUpdateDate
              ? Date.parse(prev.lastUpdateDate)
              : NaN;
            const nextTs = row?.lastUpdateDate
              ? Date.parse(row.lastUpdateDate)
              : NaN;
            if (
              !prev ||
              (Number.isFinite(nextTs) &&
                (!Number.isFinite(prevTs) || nextTs >= prevTs))
            ) {
              bestPendingByKey.set(key, row);
            }
          }

          // Pass 2: rebuild list preserving original order:
          // - include all shared rows
          // - include only the chosen pending row per key
          const emittedPendingKeys = new Set<string>();
          const out: any[] = [];
          for (const row of rows) {
            if (isSharedRow(row)) {
              out.push(row);
              continue;
            }
            const key = keyForRow(row);
            if (emittedPendingKeys.has(key)) continue;
            if (bestPendingByKey.get(key) === row) {
              out.push(row);
              emittedPendingKeys.add(key);
            }
          }
          return out;
        };

        const digitalDataRaw: any =
          await SharePageService.getCustomerSharing(resolvedCustomerId);
        const digitalData: any[] = Array.isArray(digitalDataRaw)
          ? dedupeSharingRows(digitalDataRaw)
          : [];

        const customerData = JSON.parse(
          localStorage.getItem("customerData") as string
        );

        const freePostDataRaw: any = await SharePageService.getFreePostData(
          customerData.customerId
        );
        const freePostData: any[] = Array.isArray(freePostDataRaw)
          ? dedupeSharingRows(freePostDataRaw)
          : [];

        const data = [
          ...(await Promise.all(
            digitalData.map(async (company: any) => {
              if (!company.lenderName) {
                return {};
              }

              // const correspondingDebt = debts.find((entry: any) => {
              //   return entry.id == company.debtId;
              // });

              let type = "all";

              const obj: Company = {
                id: company.id,
                name: company.lenderName,
                reference: company.accountNumber,
                type: type as Company["type"],
                address: company.address || "",
                documents: company.documents || [],
                selectedDocuments: [],
                lastShared: company.lastShared || "",
                raw: company,
              };

              return obj;
            })
          )),

          ...freePostData.map((company: any) => {
            let type = "all";

            const shareDate = company.lastSharedDate || company.submitDate;

            let obj: Company = {
              id: company.id,
              name: company.companyName,
              reference: company.referenceNumber,
              type: type as Company["type"],
              address: company.address || "",
              documents: company.documents || [],
              selectedDocuments: [],
              lastShared: company.lastShared || "",
              raw: company,
              isFp: true,
            };

            return obj;
          }),
        ];

        return data;
      } catch (error) {
        console.error("Failed to fetch companies", error);
        return [];
      }
    };

    fetchCompanies().then((companiesArray) => {
      setCompanies(companiesArray);
    });
  }, []);

  return (
    <DashboardLayout backgroundColor="#f8f9fa">
      <Box data-testid="pages-private-from-dashboard-free-post-page-container-action-1" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box data-testid="pages-private-from-dashboard-free-post-page-container-action-2">
          <Typography data-testid="pages-private-from-dashboard-free-post-page-text-freepost"
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "24px", md: "30px" },
              lineHeight: "36px",
              color: "#343a40",
              mb: 0.5,
            }}
          >
            Freepost
          </Typography>
          <Typography data-testid="pages-private-from-dashboard-free-post-page-text-action"
            variant="body1"
            sx={{ fontSize: "16px", lineHeight: "24px", color: "#6c757d" }}
          >
            If your company isn’t on our online sharing list, we can print and
            post your budget to them for free.
          </Typography>
        </Box>

        <Paper data-testid="pages-private-from-dashboard-free-post-page-container-action-3"
          sx={{
            p: 3,
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            border: "1px solid #e9ecef",
          }}
        >
          <FreepostForm onBack={() => navigate(-1)} companies={companies} />
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default FreePostPage;
